/**
 * Equipment Document Events - Betrieb Müller (v2)
 *
 * Events related to equipment inspections and certifications.
 *
 * AGGREGATION RULE: If multiple objects trigger the same obligation,
 * generate exactly ONE aggregate event. Individual object-level events
 * MUST NOT appear in the feed in parallel.
 */

import type { MockControlEvent } from '@hofsync/design-system';
import { hoursAgo, daysFromNow, monthsFromNow } from '../farm-profile';
import { STVZO_29, PFLSCHG_11 } from '../../legal';
import { EQUIPMENT_GUELLEFASS, EQUIPMENT_TRAKTOR, EQUIPMENT_ANHAENGER } from '../entities/equipment';

/**
 * Event: Feldspritze Prüfdienst (INFO)
 *
 * Note: This is Geräteprüfung (PflSchG § 11), NOT TÜV, so it's a separate obligation
 * and can appear alongside the TÜV aggregate without violating aggregation rules.
 */
export const EVENT_SPRITZE_PRUFDIENST: MockControlEvent = {
  id: 'evt-spritze-prufdienst',
  type: 'obligation-due',
  priority: 'info',
  title: 'Feldspritze: Prüfdienst fällig Mai 2026',
  subtitle: 'Geräteprüfung',
  timestamp: hoursAgo(72),
  relevantDate: monthsFromNow(4),
  relatedEntity: { name: 'Feldspritze Amazone UX 4200', href: '#equipment-spritze' },
  categories: ['pflanzenschutz', 'fristen'],
  controlType: 'Geräteprüfung',

  // === TWO-COLUMN LAYOUT ===
  isAggregate: true,
  hideAggregateBadge: true,
  affectedEntities: [
    {
      id: 'equipment-spritze',
      name: 'Feldspritze Amazone UX 4200',
      type: 'equipment',
      severity: 'info',
      status: 'Prüfung fällig Mai 2026',
      action: { label: 'Termin buchen', href: '/my-farm/equipment/spritze' },
    },
  ],
  domainLink: {
    label: 'Gerät anzeigen',
    href: '/my-farm/equipment/spritze',
  },

  // === WHY THIS MATTERS ===
  reasoning: {
    summary: 'Pflanzenschutzgeräte müssen alle 3 Jahre geprüft werden.',
    legalBasis: [PFLSCHG_11],
    factors: [
      { label: 'Gerät', value: 'Amazone UX 4200' },
      { label: 'Letzte Prüfung', value: '05/2023' },
      { label: 'Fälligkeit', value: '05/2026' },
      { label: 'Prüfzyklus', value: '3 Jahre' },
    ],
    confidence: 'high',
  },
  entities: [],
};

/**
 * TÜV Aggregate Event (WARNING)
 *
 * AGGREGATION: Single event for all TÜV-obligated vehicles.
 * Priority elevated to 'warning' because Güllefass has warning severity.
 * Individual vehicle events (like evt-tuev-guellefass) are NOT exposed separately.
 */
export const EVENT_TUEV_AGGREGATE: MockControlEvent = {
  id: 'evt-tuev-aggregate',
  type: 'obligation-due',
  priority: 'warning',
  title: 'Fahrzeug-TÜV: 1 Termin in 6 Wochen',
  subtitle: 'Fahrzeugprüfung',
  timestamp: hoursAgo(96),
  relevantDate: daysFromNow(41), // Next due: Güllefass 08.03.2026
  relatedEntity: { name: 'Fahrzeuge', href: '#equipment' },
  categories: ['tuev'],
  controlType: 'Fahrzeugprüfung',
  controlReadiness: {
    status: 'warning',
    metCount: 3,
    totalCount: 4,
    label: '3 von 4 aktuell',
  },
  reasoning: {
    summary: 'Fahrzeuge mit Straßenzulassung benötigen regelmäßige Hauptuntersuchung.',
    legalBasis: [STVZO_29],
    actionRecommendation:
      'TÜV-Termin online buchen (tuev-nord.de oder dekra.de) oder direkt bei Ihrer Werkstatt anfragen. ' +
      'Für Anhänger/Güllefass: Prüfung auch auf dem Betrieb möglich (Mobilprüfung).',
    factors: [
      { label: 'Fahrzeuge gesamt', value: '4' },
      { label: 'Davon aktuell', value: '3' },
      { label: 'Nächster Termin', value: '08.03.2026' },
      { label: 'Prüfzyklus', value: '24 Monate' },
    ],
    confidence: 'high',
  },
  entities: [EQUIPMENT_GUELLEFASS, EQUIPMENT_TRAKTOR, EQUIPMENT_ANHAENGER],
  // Aggregate event properties
  isAggregate: true,
  hideAggregateBadge: true,
  affectedEntities: [
    {
      id: 'equipment-guellefass',
      name: 'Güllefass Kotte VT 18.000',
      type: 'equipment',
      severity: 'warning',
      status: 'TÜV fällig 08.03.2026',
      dueDate: new Date('2026-03-08'),
      action: { label: 'TÜV-Termin buchen', href: '/my-farm/equipment/guellefass' },
    },
    {
      id: 'equipment-anhaenger',
      name: 'Anhänger Krampe Big Body',
      type: 'equipment',
      severity: 'info',
      status: 'TÜV bis 07/2026',
      action: { label: 'Details anzeigen', href: '/my-farm/equipment/anhaenger' },
    },
    {
      id: 'equipment-traktor',
      name: 'Traktor Fendt 724',
      type: 'equipment',
      severity: 'info',
      status: 'TÜV bis 10/2026',
      action: { label: 'Details anzeigen', href: '/my-farm/equipment/traktor' },
    },
    {
      id: 'equipment-spritze',
      name: 'Feldspritze Amazone',
      type: 'equipment',
      severity: 'info',
      status: 'Spritzen-TÜV bis 05/2027',
      action: { label: 'Details anzeigen', href: '/my-farm/equipment/spritze' },
    },
  ],
  domainLink: {
    label: 'Alle Fahrzeuge anzeigen',
    href: '/my-farm/equipment',
  },
};

/**
 * All equipment document events
 *
 * Note: Only aggregate events are exported. Individual entity events
 * (like a single-vehicle TÜV warning) are NOT exported to the feed.
 */
export const EQUIPMENT_DOC_EVENTS: MockControlEvent[] = [
  EVENT_TUEV_AGGREGATE,
  EVENT_SPRITZE_PRUFDIENST,
];
